import React, {
  createContext,
  useState,
  useContext,
  ReactNode,
  useCallback,
  useEffect,
} from 'react';
import FooterImageService, { FooterImage } from 'app/apiService/footerImage/footerImageApiService';

interface FooterImageContextType {
  loading: boolean;
  error: string;
  footerImages: FooterImage[];
  addFooterImage: (image: any) => Promise<void>;
  deleteFooterImage: (id: string) => Promise<void>;
  editFooterImage: (id: string, data: any) => Promise<void>;
  getFooterImage: (id: string) => Promise<void>;
  refetchFooterImages: () => void;
}

export const FooterImageContext = createContext<FooterImageContextType | undefined>(undefined);

export const FooterImageProvider = ({ children }: { children: ReactNode }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [footerImages, setFooterImages] = useState<FooterImage[]>([]);

  // Fetch all footer images
  const fetchFooterImages = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await FooterImageService.getAllFooterImages();
      setFooterImages(res.body || []);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch footer images');
    } finally {
      setLoading(false);
    }
  }, []);

  const addFooterImage = async (image: any) => {
    setLoading(true);
    setError('');
    try {
      await FooterImageService.addFooterImage(image);
      fetchFooterImages();
    } catch (err: any) {
      setError(err.message || 'Failed to add footer image');
    } finally {
      setLoading(false);
    }
  };

  const deleteFooterImage = async (id: string) => {
    setLoading(true);
    setError('');
    try {
      await FooterImageService.deleteFooterImage(id);
      fetchFooterImages();
    } catch (err: any) {
      setError(err.message || 'Failed to delete footer image');
    } finally {
      setLoading(false);
    }
  };

  const editFooterImage = async (id: string, data: any) => {
    setLoading(true);
    setError('');
    try {
      await FooterImageService.editFooterImage(id, data);
      fetchFooterImages();
    } catch (err: any) {
      setError(err.message || 'Failed to edit footer image');
    } finally {
      setLoading(false);
    }
  };

  const getFooterImage = async (id: string) => {
    setLoading(true);
    setError('');
    try {
      await FooterImageService.getFooterImage(id);
    } catch (err: any) {
      setError(err.message || 'Failed to get footer image');
    } finally {
      setLoading(false);
    }
  };

  const refetchFooterImages = () => {
    fetchFooterImages();
  };

  return (
    <FooterImageContext.Provider
      value={{
        loading,
        error,
        footerImages,
        addFooterImage,
        deleteFooterImage,
        editFooterImage,
        getFooterImage,
        refetchFooterImages,
      }}
    >
      {children}
    </FooterImageContext.Provider>
  );
};

export const useFooterImage = (): FooterImageContextType => {
  const context = useContext(FooterImageContext);
  if (!context) {
    throw new Error('useFooterImage must be used within a FooterImageProvider');
  }
  return context;
}; 